import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  InviteDeliveryMethod,
  type CreateInviteInput,
  type PersonSummaryDto,
  type RegistrationInviteDto,
} from '@ciimar/shared';
import { api, ApiError } from '../api/client';
import { SimilarNames } from '../components/SimilarNames';
import { useT } from '../lib/i18n';

type Mode = InviteDeliveryMethod | 'DIRECT' | null;

export function NewRegistration() {
  const t = useT();
  const [mode, setMode] = useState<Mode>(null);

  return (
    <section>
      <div className="page-head">
        <div className="page-head-text">
          <h2>{t('onboarding.new.title')}</h2>
          <p className="page-sub">{t('onboarding.new.subtitle')}</p>
        </div>
      </div>
      {mode === null && <ChoiceScreen onPick={setMode} />}
      {mode === 'DIRECT' && <DirectForm onBack={() => setMode(null)} />}
      {mode !== null && mode !== 'DIRECT' && (
        <InviteForm method={mode} onBack={() => setMode(null)} />
      )}
    </section>
  );
}

function ChoiceScreen({ onPick }: { onPick: (m: Mode) => void }) {
  const t = useT();
  return (
    <div className="card">
      <p className="muted">{t('onboarding.new.choice.prompt')}</p>
      <div className="choice-grid">
        <Choice
          title={t('onboarding.new.choice.emailTitle')}
          desc={t('onboarding.new.choice.emailDesc')}
          onClick={() => onPick(InviteDeliveryMethod.Email)}
        />
        <Choice
          title={t('onboarding.new.choice.linkTitle')}
          desc={t('onboarding.new.choice.linkDesc')}
          onClick={() => onPick(InviteDeliveryMethod.Link)}
        />
        <Choice
          title={t('onboarding.new.choice.directTitle')}
          desc={t('onboarding.new.choice.directDesc')}
          onClick={() => onPick('DIRECT')}
        />
      </div>
    </div>
  );
}

function Choice({ title, desc, onClick }: { title: string; desc: string; onClick: () => void }) {
  return (
    <button type="button" className="choice" onClick={onClick}>
      <span className="choice-title">{title}</span>
      <span className="choice-desc">{desc}</span>
    </button>
  );
}

/** Debounced collaborator search + select, for choosing the responsible person. */
function PersonPicker({
  selected,
  onSelect,
}: {
  selected: PersonSummaryDto | null;
  onSelect: (p: PersonSummaryDto | null) => void;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PersonSummaryDto[]>([]);

  useEffect(() => {
    const term = query.trim();
    if (selected || term.length < 2) {
      setResults([]);
      return;
    }
    let active = true;
    const handle = setTimeout(async () => {
      try {
        const people = await api.listPeople(term);
        if (active) setResults(people.slice(0, 8));
      } catch {
        if (active) setResults([]);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [query, selected]);

  if (selected) {
    return (
      <div className="actions-row" style={{ gap: 8, marginTop: 4 }}>
        <span className="badge approved">
          {selected.fullName}
          {selected.code ? ` · ${selected.code}` : ''}
        </span>
        <button
          type="button"
          className="link-button"
          onClick={() => {
            onSelect(null);
            setQuery('');
          }}
        >
          {t('onboarding.new.picker.change')}
        </button>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('onboarding.new.picker.placeholder')}
        autoComplete="off"
      />
      {results.length > 0 && (
        <ul className="picker-list">
          {results.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="picker-item"
                onClick={() => {
                  onSelect(p);
                  setResults([]);
                }}
              >
                <span>{p.fullName}</span>
                <span className="muted">
                  {[p.code, p.professionalEmail].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function InviteForm({ method, onBack }: { method: InviteDeliveryMethod; onBack: () => void }) {
  const t = useT();
  const [targetEmail, setTargetEmail] = useState('');
  const [responsible, setResponsible] = useState<PersonSummaryDto | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<RegistrationInviteDto | null>(null);
  const [copied, setCopied] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const input: CreateInviteInput = {
        deliveryMethod: method,
        targetEmail: method === InviteDeliveryMethod.Email ? targetEmail : undefined,
        responsiblePersonId: responsible?.id,
        responsibleName: responsible?.fullName,
        note: note || undefined,
      };
      setCreated(await api.createInvite(input));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('onboarding.new.invite.createError'));
    } finally {
      setBusy(false);
    }
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  if (created) {
    const host = safeHost(created.url);
    return (
      <div className="card success" style={{ textAlign: 'left' }}>
        <h3>{t('onboarding.new.invite.createdTitle')}</h3>
        {method === InviteDeliveryMethod.Email && (
          <p>
            {t('onboarding.new.invite.sentEmailPre')}
            <strong>{created.targetEmail}</strong>
            {t('onboarding.new.invite.sentEmailPost')}
          </p>
        )}
        {method === InviteDeliveryMethod.Link && (
          <>
            <p>{t('onboarding.new.invite.shareLink')}</p>
            <div className="link-box">
              <input readOnly value={created.url ?? ''} onFocus={(e) => e.target.select()} />
              <button type="button" onClick={() => void copy(created.url ?? '')}>
                {copied ? t('onboarding.new.invite.copied') : t('onboarding.new.invite.copy')}
              </button>
            </div>
          </>
        )}
        {method === InviteDeliveryMethod.Code && (
          <>
            <p>
              {t('onboarding.new.invite.codePre')}
              {host ? <strong>{host}</strong> : 'onboarding.ciimar.up.pt'}
              {t('onboarding.new.invite.codePost')}
            </p>
            <div className="code-box">
              <span className="code-value">{created.code}</span>
              <button type="button" onClick={() => void copy(created.code ?? '')}>
                {copied ? t('onboarding.new.invite.copied') : t('onboarding.new.invite.copy')}
              </button>
            </div>
          </>
        )}
        <p className="muted">{t('onboarding.new.invite.expiresNote')}</p>
        <button type="button" onClick={onBack}>
          {t('onboarding.new.invite.createAnother')}
        </button>
      </div>
    );
  }

  const title =
    method === InviteDeliveryMethod.Email
      ? t('onboarding.new.invite.titleEmail')
      : t('onboarding.new.invite.titleLink');

  return (
    <form className="card" onSubmit={onSubmit}>
      <h3>{title}</h3>
      {error && <div className="alert error">{error}</div>}

      {method === InviteDeliveryMethod.Email && (
        <label>
          {t('onboarding.new.invite.emailLabel')}
          <input
            required
            type="email"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            autoComplete="off"
          />
        </label>
      )}

      <p className="muted" style={{ marginTop: 16 }}>
        {t('onboarding.new.invite.responsibleHelp')}
      </p>
      <PersonPicker selected={responsible} onSelect={setResponsible} />
      <label>
        {t('onboarding.new.invite.internalNote')}
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>

      <div className="actions-row">
        <button type="button" className="link-button" onClick={onBack} disabled={busy}>
          ← {t('onboarding.new.chooseAnother')}
        </button>
        <button type="submit" disabled={busy}>
          {busy
            ? t('onboarding.new.invite.submitting')
            : method === InviteDeliveryMethod.Email
              ? t('onboarding.new.invite.submitEmail')
              : t('onboarding.new.invite.submitLink')}
        </button>
      </div>
    </form>
  );
}

/**
 * Presencial direct entry: creates the collaborator record immediately (like
 * the "Novo colaborador" button in Pessoas) and opens the ficha to complete the
 * remaining fields — it does NOT go through the onboarding validation queue.
 */
function DirectForm({ onBack }: { onBack: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!fullName.trim()) return;
    setError(null);
    setBusy(true);
    try {
      const person = await api.createPerson({
        fullName: fullName.trim(),
        professionalEmail: email.trim() || undefined,
      });
      // Straight to the ficha to complete the rest, as with "Novo colaborador".
      navigate(`/people/${person.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('onboarding.new.direct.createError'));
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={onSubmit}>
      <h3>{t('onboarding.new.direct.title')}</h3>
      <p className="muted">{t('onboarding.new.direct.help')}</p>
      {error && <div className="alert error">{error}</div>}
      <label>
        {t('onboarding.new.direct.fullName')}
        <input required autoFocus value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </label>
      <div style={{ marginTop: 4 }}>
        <SimilarNames name={fullName} />
      </div>
      <label>
        {t('onboarding.new.direct.professionalEmail')}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <div className="actions-row">
        <button type="button" className="link-button" onClick={onBack} disabled={busy}>
          ← {t('onboarding.new.chooseAnother')}
        </button>
        <button type="submit" disabled={busy}>
          {busy ? t('onboarding.new.direct.submitting') : t('onboarding.new.direct.submit')}
        </button>
      </div>
    </form>
  );
}

function safeHost(url?: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}
